import { memo, useCallback, useState } from 'react';
import { Text } from '@library';
import useSearchParamFilterHelper from '../../hooks/useSearchParamFilterHelper';
import useProductFilterCount from '../../queries/useProductFilterCount';
import BrandImage from '../../../brand/components/BrandImage';
import withErrorBoundary from '../../../core/components/errors/withErrorBoundary';
import useBrands from '../../../brand/queries/useBrands';
import styles from './Filter.module.scss';
import FilterWrapper from './FilterWrapper';
import { useFilteredValues } from './Filter';
import FilterCheckbox from './FilterCheckbox';

const FilterBrand = withErrorBoundary(
    memo(
        ({
            name,
            field,
            defaultExpand,
        }: {
            name: string;
            field: string;
            defaultExpand?: boolean;
        }) => {
            const { updateCurrentValues, selectedValues } =
                useSearchParamFilterHelper()(field);
            const [search, setSearch] = useState('');
            const { productFilterValues } = useProductFilterCount({
                filterField: field,
            });

            const { brands } = useBrands();

            const getCount = useCallback(
                (value: string) =>
                    productFilterValues.find(item => item.value === value)
                        ?.total ?? 0,
                [productFilterValues]
            );

            const getLogo = useCallback(
                (value: string) =>
                    brands.find(brand => brand.name === value)?.logo,
                [brands]
            );

            const [filteredValues, count] = useFilteredValues({
                filterValues: productFilterValues,
                search,
                getCount,
                field,
            });

            if (count === 0) {
                return null;
            }

            return (
                <FilterWrapper
                    name={name}
                    setSearch={setSearch}
                    defaultExpand={defaultExpand}
                    items={filteredValues.map(({ value }) => ({
                        render: setShowFullList => (
                            <FilterItem
                                key={`${name}-filter-item-${value}`}
                                selectedValues={selectedValues}
                                getLogo={getLogo}
                                getCount={getCount}
                                updateCurrentValues={(
                                    newValue: string,
                                    isRemove?: boolean,
                                    isReplace?: boolean
                                ) => {
                                    setShowFullList(false);
                                    updateCurrentValues(
                                        newValue,
                                        isRemove,
                                        isReplace
                                    );
                                }}
                                value={value}
                            />
                        ),
                        isSelected: selectedValues.includes(value),
                    }))}
                />
            );
        }
    )
);

const FilterItem = memo(
    ({
        updateCurrentValues,
        selectedValues,
        value,
        getLogo,
        getCount,
    }: {
        updateCurrentValues(
            value: string,
            isRemove?: boolean,
            isReplace?: boolean
        ): void;
        selectedValues: string[];
        value: string;
        getLogo(value: string): string | undefined;
        getCount(value: string): number;
    }) => (
        <FilterCheckbox
            updateCurrentValues={updateCurrentValues}
            selectedValues={selectedValues}
            value={value}
            data-test-class="brandFilterItem"
        >
            <div className={styles.brandImage}>
                <BrandImage
                    url={getLogo(value)}
                    size={'50px'}
                    aspectRatio={1 / 2}
                    alt={value}
                />
            </div>
            <div className={styles.value} data-content={value}>
                <Text secondary>{value}</Text>
            </div>
            &nbsp;&nbsp;
            <div className={styles.count}>{getCount(value)}</div>
        </FilterCheckbox>
    )
);

export default FilterBrand;
